// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skyhold — "what actually happened", computed after the round. A perfect-information abstract has
// a knowable better answer at every turn, so the results screen can show the loser the turn where
// the game was really decided instead of just the score.
//
// Two rules this file exists to honour:
//   * NEVER MANUFACTURE A MISTAKE. If the search finds no losing blunder, it says so — "you were
//     out-positioned from the opening, not out-played in one move" — rather than picking whichever
//     turn scored worst and calling it the blunder.
//   * NEVER BLOCK THE RESULTS PAINT. The work is chunked through setTimeout (not
//     requestIdleCallback, which older Safari lacks and a hidden tab throttles), so the screen is
//     up and interactive from the first frame and the verdict arrives into it.

import { PH_PLAY, cellName, cloneState, createState, genPlies, plyA, plyBuild, plyKind, step, NONE, type State } from './rules';
import { searchTurn } from './bot';

export interface Verdict {
  done: boolean;
  progress: number;
  total: number;
  /** The turn where the loser's position collapsed, if there is a single one. */
  turn: number | null;
  playedText: string;
  betterText: string;
  swing: number;
  /** Honest summary line, always set once done. */
  summary: string;
}

const DEPTH = 2;
const MATE = 900_000;

export interface Analysis {
  verdict(): Verdict;
  cancel(): void;
}

/**
 * Re-search each of the loser's turns and find the biggest single swing. `onUpdate` fires as the
 * work progresses so the screen can show "Analysing 6 / 14" rather than freezing on a spinner.
 */
export function analyse(
  modeId: string,
  seed: number,
  log: readonly number[],
  loser: number,
  who: { name: string; isSelf: boolean },
  onUpdate: (v: Verdict) => void,
): Analysis {
  const turns: number[] = [];
  {
    const probe = createState(modeId, seed);
    for (let i = 0; i < log.length; i++) {
      if (probe.phase === PH_PLAY && probe.turn === loser && plyKind(log[i]) === 0) turns.push(i);
      step(probe, log[i]);
    }
  }

  const v: Verdict = { done: false, progress: 0, total: turns.length, turn: null, playedText: '', betterText: '', swing: 0, summary: '' };
  let cancelled = false;
  let cursor = 0;
  let bestSwing = 0;
  let bestAt = -1;
  let bestPlayed = '';
  let bestBetter = '';

  const replayTo = (i: number): State => {
    const s = createState(modeId, seed);
    for (let k = 0; k < i; k++) step(s, log[k]);
    return s;
  };

  const describe = (s: State, ply: number): string => {
    if (plyKind(ply) !== 0) return 'a placement';
    const to = plyA(ply);
    const build = plyBuild(ply);
    return build === NONE ? `climb to ${cellName(s, to)}` : `${cellName(s, to)}, build ${cellName(s, build)}`;
  };

  const chunk = (): void => {
    if (cancelled) return;
    const until = Date.now() + 22;
    while (cursor < turns.length && Date.now() < until) {
      const at = turns[cursor];
      const s = replayTo(at);
      const legal = genPlies(s);
      if (legal.length > 1) {
        const best = searchTurn(s, { depth: DEPTH, timeMs: 0, nodes: 40_000 });
        const after = cloneState(s);
        step(after, log[at]);
        let playedScore: number;
        if (after.phase === 3) {
          playedScore = after.winner === loser ? MATE : -MATE;
        } else {
          playedScore = -searchTurn(after, { depth: DEPTH - 1 >= 1 ? DEPTH - 1 : 1, timeMs: 0, nodes: 40_000 }).score;
        }
        const swing = best.score - playedScore;
        if (swing > bestSwing && best.ply !== log[at]) {
          bestSwing = swing;
          bestAt = at;
          bestPlayed = describe(s, log[at]);
          bestBetter = describe(s, best.ply);
        }
      }
      cursor++;
      v.progress = cursor;
      onUpdate(v);
    }
    if (cursor < turns.length) {
      setTimeout(chunk, 0);
      return;
    }
    v.done = true;
    // A swing has to be big enough to be a REAL mistake. Below this it is search noise, and calling
    // it a blunder would be inventing one.
    if (bestAt >= 0 && bestSwing > 90) {
      // Count the LOSER's own turns, not every ply on the board — "turn 21" in an 11-turn game is
      // the kind of number that quietly tells a player the analysis is not to be trusted.
      const turnNo = turns.indexOf(bestAt) + 1;
      const subject = who.isSelf ? 'you played' : `${who.name} played`;
      const kept = who.isSelf ? 'kept you in it' : 'would have held it';
      v.turn = turnNo;
      v.playedText = bestPlayed;
      v.betterText = bestBetter;
      v.swing = Math.round(bestSwing);
      v.summary = `Turn ${turnNo} was the one — ${subject} ${bestPlayed}. ${bestBetter} ${kept}.`;
    } else {
      v.summary = who.isSelf
        ? 'No single losing blunder — you were out-positioned from the opening, not out-played in one move.'
        : `No single losing blunder — ${who.name} was out-positioned from the opening, not out-played in one move.`;
    }
    onUpdate(v);
  };

  setTimeout(chunk, 0);
  return {
    verdict: () => v,
    cancel: () => {
      cancelled = true;
    },
  };
}
